import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { GLOBE_RADIUS, latLonToVector3 } from "../lib/seismic";
import { useSim } from "../lib/store";

const LAVA = 500;
const ASH = 900;
const WIND = new THREE.Vector3(1, 0, 0.35).normalize();

function makeState(count, kind) {
  const pos = new Float32Array(count * 3);
  const vel = new Float32Array(count * 3);
  const life = new Float32Array(count);
  for (let i = 0; i < count; i++) resetParticle(pos, vel, life, i, kind, Math.random());
  return { pos, vel, life };
}

function resetParticle(pos, vel, life, i, kind, phase) {
  const a = Math.random() * Math.PI * 2;
  if (kind === "lava") {
    const speed = 0.45 + Math.random() * 0.55;
    const spread = 0.12 + Math.random() * 0.18;
    pos[i * 3] = (Math.random() - 0.5) * 0.02;
    pos[i * 3 + 1] = 0.02;
    pos[i * 3 + 2] = (Math.random() - 0.5) * 0.02;
    vel[i * 3] = Math.cos(a) * spread;
    vel[i * 3 + 1] = speed;
    vel[i * 3 + 2] = Math.sin(a) * spread;
    life[i] = phase;
  } else {
    const speed = 0.28 + Math.random() * 0.4;
    pos[i * 3] = (Math.random() - 0.5) * 0.05;
    pos[i * 3 + 1] = 0.15 + Math.random() * 0.25;
    pos[i * 3 + 2] = (Math.random() - 0.5) * 0.05;
    vel[i * 3] = (Math.random() - 0.5) * 0.05;
    vel[i * 3 + 1] = speed;
    vel[i * 3 + 2] = (Math.random() - 0.5) * 0.05;
    life[i] = phase;
  }
}

export function VolcanoEruption({ event }) {
  const groupRef = useRef();
  const lavaRef = useRef();
  const ashRef = useRef();
  const shadowRef = useRef();
  const eruption = event?.activeEruption;

  const lava = useMemo(() => makeState(LAVA, "lava"), []);
  const ash = useMemo(() => makeState(ASH, "ash"), []);

  const shadowTex = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = c.height = 128;
    const ctx = c.getContext("2d");
    const g = ctx.createRadialGradient(64, 64, 4, 64, 64, 64);
    g.addColorStop(0, "rgba(0,0,0,1)");
    g.addColorStop(0.6, "rgba(0,0,0,0.6)");
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    return new THREE.CanvasTexture(c);
  }, []);

  const lavaGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(lava.pos, 3));
    return g;
  }, [lava]);
  const ashGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(ash.pos, 3));
    return g;
  }, [ash]);

  const orient = useMemo(() => {
    if (!eruption) return null;
    const pos = latLonToVector3(eruption.lat, eruption.lon, GLOBE_RADIUS + 0.02);
    const normal = pos.clone().normalize();
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
    return { position: pos, quaternion: q };
  }, [eruption]);

  useFrame((_, delta) => {
    const st = useSim.getState();
    if (!eruption || !groupRef.current || !st.simulating) {
      if (groupRef.current) groupRef.current.visible = false;
      return;
    }
    groupRef.current.visible = true;
    const dt = Math.min(delta, 0.05);
    const g = 0.9;

    for (let i = 0; i < LAVA; i++) {
      lava.vel[i * 3 + 1] -= g * dt;
      lava.pos[i * 3] += lava.vel[i * 3] * dt;
      lava.pos[i * 3 + 1] += lava.vel[i * 3 + 1] * dt;
      lava.pos[i * 3 + 2] += lava.vel[i * 3 + 2] * dt;
      if (lava.pos[i * 3 + 1] < 0) resetParticle(lava.pos, lava.vel, lava.life, i, "lava", 0);
    }
    lavaGeo.attributes.position.needsUpdate = true;

    for (let i = 0; i < ASH; i++) {
      const h = ash.pos[i * 3 + 1];
      ash.vel[i * 3] += WIND.x * 0.55 * h * dt;
      ash.vel[i * 3 + 2] += WIND.z * 0.55 * h * dt;
      ash.pos[i * 3] += ash.vel[i * 3] * dt;
      ash.pos[i * 3 + 1] += ash.vel[i * 3 + 1] * dt;
      ash.pos[i * 3 + 2] += ash.vel[i * 3 + 2] * dt;
      ash.vel[i * 3] *= 1.003;
      ash.vel[i * 3 + 2] *= 1.003;
      ash.vel[i * 3 + 1] *= 0.995;
      if (h > 3.8 || ash.pos[i * 3] > 3.5) resetParticle(ash.pos, ash.vel, ash.life, i, "ash", 0);
    }
    ashGeo.attributes.position.needsUpdate = true;

    if (shadowRef.current) {
      const drift = 1.0;
      shadowRef.current.position.set(WIND.x * drift, 0.003, WIND.z * drift);
      shadowRef.current.material.opacity = 0.5;
    }
  });

  if (!orient) return null;

  return (
    <group ref={groupRef} position={orient.position} quaternion={orient.quaternion}>
      <mesh ref={shadowRef} rotation={[-Math.PI / 2, 0, 0]} scale={[2.6, 1.5, 1]} position={[1, 0.003, 0]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial color="#000000" map={shadowTex} transparent opacity={0.5} depthWrite={false} />
      </mesh>
      <points ref={ashRef} geometry={ashGeo} frustumCulled={false}>
        <pointsMaterial color="#9a9a9a" size={0.07} sizeAttenuation transparent opacity={0.45} depthWrite={false} />
      </points>
      <points ref={lavaRef} geometry={lavaGeo} frustumCulled={false}>
        <pointsMaterial color="#ff7a1a" size={0.035} sizeAttenuation transparent opacity={0.8} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </points>
      <pointLight color="#ff5a00" intensity={7} distance={2.5} decay={2} position={[0, 0.05, 0]} />
    </group>
  );
}
